import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {PopupService} from "../../../services/popup.service";
import {Component, HostListener, OnInit, OnDestroy} from "@angular/core";
import {Subject, take} from "rxjs";
import {takeUntil} from "rxjs/operators";

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from '../../../state/app.state';
import { login, loginSuccess, loginFailure } from '../../../state/auth/auth.actions';
import {AuthService} from "../../../services/auth.service";

import * as SubscriptionActions from '../../../state/subscription/subscription.actions';
import {AppTourService} from "../../../services/app-tour.service";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  form: FormGroup;
  errorMessages: any;
  isLoading: boolean = false;
  show_password:boolean=true;

  private destroy$ = new Subject<void>();

  constructor(
    public formBuilder: FormBuilder,
    private authService : AuthService,
    private routerService: Router,
    private popupService: PopupService,
    private store: Store<AppState>,
    private actions$: Actions,
    private appTourService: AppTourService
  ) {

    this.form = formBuilder.group(
      {
        email: ["",
          [Validators.required, Validators.email],
        ],
        password: ["", [Validators.required]],
        remember: [false],
      },
      { updateOn: 'change'}
    );

    this.errorMessages = {
      email: [
        {
          type: "required",
          message: "Email is required",
        },
        {
          type: "email",
          message: "Value must be an email",
        },
      ],
      password: [
        {
          type: "required",
          message: "Password is required"
        },
      ],
    };

  }

  /*
  @HostListener('document:keydown.enter', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    this.processLogin();
  }
  */

  ngOnInit(): void {
    console.log('[LoginComponent] Component initialized');

    // Check if already logged in and redirect (with proper cleanup)
    this.authService.isLoggedIn()
      .pipe(take(1))
      .subscribe(isLoggedIn => {
        console.log('[LoginComponent] Initial isLoggedIn check:', isLoggedIn);
        if (isLoggedIn) {
          this.routerService.navigate(['/dashboard']);
        }
      });

    // Set up login success listener ONCE - will handle all login attempts
    this.setupLoginSuccessListener();

    // Set up login failure listener ONCE - will handle all login attempts
    this.setupLoginFailureListener();

    /*
    this.isLoading = true;
    this.userService.checkAuth().then(response => {
      this.isLoading = false;
      if (response) {
        if(this.userService.user.subscriptions)
            if(!this.userService.user.has_completed_setup){
              this.routerService.navigate([response.redirect_url]);
              }
            else {
          this.routerService.navigate([this.userService.redirectUrl]);
        }
        else{
          this.routerService.navigate([response.redirect_url]);
        }
        this.popupService.notify('Your previous session has been restored!', 'Success', "success");
      }
    }).catch(error => {
      this.isLoading = false;
    });
    */

  }

  @HostListener('document:keydown.enter', ['$event'])
onEnterPress(event: KeyboardEvent): void {
  if (this.form.valid)
    this.processLogin();

}

  /**
   * Set up login success listener - called once in ngOnInit
   * This listener will handle ALL login attempts
   * The AuthGuard will wait for the state to update, so we can navigate immediately
   */
  private setupLoginSuccessListener(): void {
    console.log('[LoginComponent] Setting up login success listener');

    this.actions$.pipe(
      ofType(loginSuccess),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (action) => {
        console.log('[LoginComponent] Login success action received', action);
        this.isLoading = false;

        const user = action.user;

        // Show notification
        this.popupService.notify('You have successfully login!', 'Login success', 'success');
        this.appTourService.resetTourStateForUserChange();

        // Dispatch subscription data
        let subscriptionData = {
          ai_tokens: user.ai_tokens,
          credits: user.credits,
          on_trial: user.on_trial,
          subscribed: user.subscribed,
        };
        this.store.dispatch(SubscriptionActions.storeSubscriptionData(subscriptionData));

        // Navigate to onboarding if setup is not complete, otherwise to dashboard
        console.log('[LoginComponent] Checking user setup status');

        if (!user.has_completed_setup) {
          console.log('[LoginComponent] User has not completed setup, redirecting to /onboarding');
          this.routerService.navigate(['/onboarding']);
        } else {
          console.log('[LoginComponent] User setup complete, redirecting to /dashboard');
          this.routerService.navigate(['/dashboard']);
        }
      },
      error: (error) => {
        console.error('[LoginComponent] Login success stream error:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Set up login failure listener - called once in ngOnInit
   * This listener will handle ALL login failures
   */
  private setupLoginFailureListener(): void {
    console.log('[LoginComponent] Setting up login failure listener');

    this.actions$.pipe(
      ofType(loginFailure),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (action) => {
        console.log('[LoginComponent] Login failure received', action);
        this.isLoading = false;

        // Extract error message from the error object
        let errorMessage = 'An error occurred during login';
        const error = action.error as any;

        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error?.error?.error?.message) {
          errorMessage = error.error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.popupService.notify(errorMessage, 'Login error', 'error');
      },
      error: (error) => {
        console.error('[LoginComponent] Login failure listener error:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * @desc Login form handler
   */
  processLogin() {
    console.log('[LoginComponent] processLogin called');

    let params = {
      email: this.form.value.email,
      password: this.form.value.password,
    };

    this.form.markAllAsTouched();

    if(this.form.valid){
      this.isLoading = true;
      console.log('[LoginComponent] Dispatching login action, isLoading:', this.isLoading);

      // Dispatch login action - the listeners set up in ngOnInit will handle the response
      this.store.dispatch(login({ email: params.email, password: params.password }));
    } else {
      console.log('[LoginComponent] Form is invalid');
    }
  }

  showPassword() {
    this.show_password = !this.show_password;
    return this.show_password
  }

  ngOnDestroy(): void {
    console.log('[LoginComponent] Component being destroyed');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @desc Call the username and password authentication via API
   * @param params
   * @private
   */
  /*
  private login(params) {
    this.isLoading = true;
    this.userService.login(params).then(response => {
      // Login successful
      this.userService.checkAuth().then(response => {
        this.userService.loggedIn = true;
        console.log(this.userService.user)
        if(!response.redirect_url) {
          const redirectUrl = this.userService.redirectUrl || '/dashboard';
          this.routerService.navigate([redirectUrl]);
          this.isLoading = false;
          this.userService.redirectUrl = null;
        }
        else {
          console.log("response",response);

          window.location.href=environment.billing_url;
          this.isLoading = false;
          this.userService.redirectUrl = null;
        }

      }).catch(error => {
        console.log(error)
        this.popupService.error("Error", "Wrong credentials", "Try again");
        this.isLoading = false;
      })
    }).catch(error => {
      console.log("error",error)
      this.popupService.error("Error", "Wrong credentials", "Try again");
      this.isLoading = false;
    })
  }
  */

}
